// src/CartContext.js
import React, { createContext, useContext, useRef, useEffect, useState } from "react";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const inactivityTimer = useRef(null);

    // Load cart drom localstorage
    const [cart, setCart] = useState(() => {
        const savedCart = localStorage.getItem("cart");
        return savedCart ? JSON.parse(savedCart) : [];
    });

    // save cart to localstorage
    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(cart));
    }, [cart]);


    // Reset if inactivity for 15 minutes
    const resetCart = () => {
        setCart([]);
    }

    // Inactivity timer
    const resetInactivityTimer = () => {
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
        }
        inactivityTimer.current = setTimeout(resetCart, 15 * 60 * 1000);
    }

    // Monitor user activity (clicks, key presses, mouse movement)
    useEffect(() => {
        const handleActivity = () => resetInactivityTimer();
        window.addEventListener("mousemove", handleActivity);
        window.addEventListener("keydown", handleActivity);
        window.addEventListener("click", handleActivity);

        // Cleanup listeners and timer when unmounted
        return () => {
            window.removeEventListener("mousemove", handleActivity);
            window.removeEventListener("keydown", handleActivity);
            window.removeEventListener("click", handleActivity);
            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
            }
        };
    }, []);

    const addToCart = (product) => {
        setCart((prevCart) => {
            const existingItem = prevCart.find(
                (item) =>
                    item.id === product.id &&
                    item.variant.color === product.variant.color &&
                    item.variant.model === product.variant.model
            );

            if (existingItem) {
                // If already item available, update the quantity
                return prevCart.map((item) =>
                    item.id === product.id &&
                        item.variant.color === product.variant.color &&
                        item.variant.model === product.variant.model
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            } else {
                return [...prevCart, { ...product, quantity: 1 }];
            }
        });
    };

    const removeFromCart = (id, variant) => {
        setCart((prevCart) =>
            prevCart.filter(
                (item) =>
                    !(item.id === id &&
                        item.variant.color === variant.color &&
                        item.variant.model === variant.model)
            )
        );
    };

    const updateQuantity = (id, variant, quantity) => {
        setCart((prevCart) =>
            prevCart.map((item) =>
                item.id === id && item.variant.color === variant.color && item.variant.model === variant.model
                    ? { ...item, quantity: Math.max(1, quantity) } : item
            )
        );
    };

    // Clear Cart function (✅ added)
    const clearCart = () => {
        setCart([]);
    };

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
